import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  alpha
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  CloudDownload as SaveIcon,
  Check as CheckIcon,
  Visibility as MaskIcon,
  Delete as DeleteIcon,
  Info as MetadataIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

export interface GeneratedImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  timestamp: string;
  metadata?: {
    width?: number;
    height?: number;
    transferType?: string;
    modelUsed?: string;
    elapsedTime?: number;
    quality?: 'low' | 'high';
  };
  maskFiles?: {
    humanMask?: string;
    humanMaskExpanded?: string;
    humanImageMask?: string;
    garmentMask?: string;
  };
}

interface GeneratedImageViewerProps {
  images: GeneratedImage[];
  loading: boolean;
  error?: string | null;
  onSaveImage?: (image: GeneratedImage) => void;
  onDeleteImage?: (image: GeneratedImage) => void;
}

const GeneratedImageViewer: React.FC<GeneratedImageViewerProps> = ({
  images,
  loading,
  error,
  onSaveImage,
  onDeleteImage
}) => {
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [maskDialogOpen, setMaskDialogOpen] = useState(false);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleOpenDialog = (image: GeneratedImage) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenMaskDialog = (image: GeneratedImage) => {
    setSelectedImage(image);
    setMaskDialogOpen(true);
  };

  const handleCloseMaskDialog = () => {
    setMaskDialogOpen(false);
  };

  const handleOpenMetadataDialog = (image: GeneratedImage) => {
    setSelectedImage(image);
    setMetadataDialogOpen(true);
  };

  const handleCloseMetadataDialog = () => {
    setMetadataDialogOpen(false);
  };

  const handleDeleteImage = (image: GeneratedImage) => {
    if (onDeleteImage && window.confirm('Czy na pewno chcesz usunąć ten obraz i powiązane maski?')) {
      onDeleteImage(image);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = (image: GeneratedImage, filename?: string) => {
    // Generate filename using project name and generation ID if not provided
    if (!filename) {
      // Extract project name from URL path (/user/username/project/PROJECT_NAME/...)
      const urlParts = image.url.split('/');
      const projectIndex = urlParts.findIndex(part => part === 'project');
      const projectName = projectIndex !== -1 && projectIndex + 1 < urlParts.length 
        ? urlParts[projectIndex + 1] 
        : 'project';
      
      // Use generation ID (image.id) and project name for filename
      const generationId = image.id || 'generated';
      filename = `${projectName}-${generationId}.png`;
    }
    
    // Use the complete garment-and-human image for download (shows model wearing the garment)
    // If current URL is the human-only version, try to get the complete version instead
    const completePreviewUrl = image.url.includes('output-preview-human.png') 
      ? image.url.replace('output-preview-human.png', 'output-preview-garment-and-human.png')
      : image.url;
    
    const link = document.createElement('a');
    link.href = completePreviewUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date helper function
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  // Get mask files for an image - use provided maskFiles or fallback to generated paths
  const getMaskFiles = (image: GeneratedImage) => {
    // First, try to use the mask files provided directly from the API
    if (image.maskFiles && Object.keys(image.maskFiles).length > 0) {
      return {
        humanMask: image.maskFiles.humanMask,
        humanMaskExpanded: image.maskFiles.humanMaskExpanded,
        humanImageMask: image.maskFiles.humanImageMask,
        garmentMask: image.maskFiles.garmentMask
      };
    }
    
    // Fallback to generating mask URLs from image URL (for backward compatibility)
    const imageUrl = image.url;
    
    // Change /images/ to /masks/ in path; also support preview paths
    const maskBasePath = imageUrl
      .replace('/images/', '/masks/')
      .replace('/preview/', '/preview/');
    
    // Extract directory path
    const maskDir = maskBasePath.substring(0, maskBasePath.lastIndexOf('/'));
    
    // If selected image is canonical garment+human preview
    if (imageUrl.includes('output-preview-garment-and-human')) {
      return {
        humanMask: `${maskDir}/human-mask.png`,
        humanMaskExpanded: `${maskDir}/human-mask-after-expand.png`,
        humanImageMask: `${maskDir}/human-image-mask.png`,
        garmentMask: `${maskDir}/garment-mask.png`
      };
    }

    // If selected image is already a mask file, return available canonical mask set in same dir
    const lower = imageUrl.toLowerCase();
    if (lower.includes('/masks/') || lower.includes('mask')) {
      return {
        humanMask: `${maskDir}/human-mask.png`,
        humanMaskExpanded: `${maskDir}/human-mask-after-expand.png`,
        humanImageMask: `${maskDir}/human-image-mask.png`,
        garmentMask: `${maskDir}/garment-mask.png`
      };
    }
    
    // Fallback to old structure for backward compatibility
    const filename = maskBasePath.split('/').pop() || '';
    const filenameNoExt = filename.replace(/\.png$/, '');
    
    // Extract base name: output-YYYYMMDD-HHMMSS from pattern like output-20250614-125519-186-Preview Image
    const parts = filenameNoExt.split('-');
    let baseName = '';
    
    if (parts.length >= 3 && parts[0] === 'output') {
      // Take first 3 parts: output-YYYYMMDD-HHMMSS
      baseName = parts.slice(0, 3).join('-');
    } else {
      // Fallback: try canonical names in same dir
      return {
        humanMask: `${maskDir}/human-mask.png`,
        humanMaskExpanded: `${maskDir}/human-mask-after-expand.png`,
        humanImageMask: `${maskDir}/human-image-mask.png`,
        garmentMask: `${maskDir}/garment-mask.png`
      };
    }
    
    return {
      humanMask: `${maskDir}/${baseName}-165-human-mask.png`,
      humanMaskExpanded: `${maskDir}/${baseName}-166-human-mask-after-expand.png`,
      humanImageMask: `${maskDir}/${baseName}-162-human-image-mask.png`,
      garmentMask: `${maskDir}/${baseName}-175-garment-mask.png`
    };
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        py: 8 
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Generowanie obrazów...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          To może potrwać do 60 sekund
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Paper 
        elevation={0} 
        variant="outlined"
        sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: 2,
          borderColor: theme.palette.error.light,
          bgcolor: alpha(theme.palette.error.light, 0.05)
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Wystąpił błąd podczas generowania obrazów
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="outlined" color="primary">
          Spróbuj ponownie
        </Button>
      </Paper>
    );
  }

  if (images.length === 0) {
    return (
      <Paper 
        elevation={0} 
        variant="outlined"
        sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: 2 
        }}
      >
        <Typography variant="h6" gutterBottom>
          Brak wygenerowanych obrazów
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Skonfiguruj parametry i kliknij "Generuj obraz" aby zobaczyć wyniki
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Wygenerowane obrazy ({images.length})
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Kliknij na obraz, aby zobaczyć pełny rozmiar
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {images.map((image) => (
          <Grid item xs={12} sm={6} md={4} key={image.id}>
            <Card 
              variant="outlined" 
              sx={{ 
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height={240}
                  image={image.thumbnailUrl || image.url}
                  alt="Generated image"
                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => handleOpenDialog(image)}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)'
                    }
                  }}
                  onClick={() => handleOpenDialog(image)}
                >
                  <ZoomInIcon />
                </IconButton>
                <Chip
                  label={image.metadata?.quality === 'high' ? 'Wysoka jakość' : 'Standardowa jakość'}
                  size="small"
                  color={image.metadata?.quality === 'high' ? 'primary' : 'default'}
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    '& .MuiChip-label': {
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
              
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Wygenerowano: {formatDate(image.timestamp)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  {image.metadata?.transferType && `Typ: ${image.metadata.transferType}`}
                </Typography>
                
                {image.metadata?.elapsedTime && (
                  <Typography variant="body2" color="text.secondary">
                    Czas generowania: {image.metadata.elapsedTime}s
                  </Typography>
                )}
                
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(image)}
                  >
                    Pobierz
                  </Button>
                  
                  <Button
                    size="small"
                    color="secondary"
                    startIcon={<MaskIcon />}
                    onClick={() => handleOpenMaskDialog(image)}
                  >
                    Mask
                  </Button>

                  {isAdmin && (
                    <Button
                      size="small"
                      color="info"
                      startIcon={<MetadataIcon />}
                      onClick={() => handleOpenMetadataDialog(image)}
                    >
                      Metadata
                    </Button>
                  )}
                  
                  {onSaveImage && (
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => onSaveImage(image)}
                    >
                      Zapisz
                    </Button>
                  )}
                  
                  {onDeleteImage && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteImage(image)}
                    >
                      Usuń
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Image Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        {selectedImage && (
          <>
            <DialogContent sx={{ p: 0, position: 'relative' }}>
              <img
                src={selectedImage.url}
                alt="Full size generated image"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
              
              <Box sx={{ 
                position: 'absolute', 
                bottom: 0, 
                left: 0, 
                right: 0, 
                p: 2,
                bgcolor: 'rgba(0,0,0,0.7)', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="body2">
                  {selectedImage.metadata?.width} × {selectedImage.metadata?.height} pixels
                </Typography>
                
                <Box>
                  <IconButton 
                    color="inherit" 
                    onClick={() => handleCopyLink(selectedImage.url)}
                    sx={{ mr: 1 }}
                  >
                    {copied ? <CheckIcon /> : <CopyIcon />}
                  </IconButton>
                  
                  <IconButton 
                    color="inherit" 
                    onClick={() => handleDownload(selectedImage)}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseDialog}>Zamknij</Button>
              
              {onSaveImage && (
                <Button 
                  color="primary" 
                  variant="contained" 
                  startIcon={<SaveIcon />}
                  onClick={() => {
                    onSaveImage(selectedImage);
                    handleCloseDialog();
                  }}
                >
                  Zapisz do projektu
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Mask Preview Dialog */}
      <Dialog
        open={maskDialogOpen}
        onClose={handleCloseMaskDialog}
        maxWidth="lg"
        fullWidth
      >
        {selectedImage && (
          <>
            <DialogContent>
              <Typography variant="h6" gutterBottom>
                Podgląd masek dla obrazu
              </Typography>
              
              <Grid container spacing={2}>
                {(() => {
                  const maskFiles = getMaskFiles(selectedImage);
                  const masks = [
                    { name: 'Human Mask', url: maskFiles.humanMask },
                    { name: 'Human Mask (Expanded)', url: maskFiles.humanMaskExpanded },
                    { name: 'Human Image Mask', url: maskFiles.humanImageMask },
                    { name: 'Garment Mask', url: maskFiles.garmentMask }
                  ];
                  
                  return masks.map((mask, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Paper elevation={2} sx={{ p: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {mask.name}
                        </Typography>
                        <Box
                          component="img"
                          src={mask.url}
                          alt={mask.name}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            bgcolor: 'grey.100'
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const errorText = document.createElement('div');
                              errorText.textContent = 'Maska niedostępna';
                              errorText.style.cssText = 'display:flex;align-items:center;justify-content:center;height:200px;color:#666;';
                              parent.appendChild(errorText);
                            }
                          }}
                        />
                      </Paper>
                    </Grid>
                  ));
                })()}
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Data wygenerowania: {formatDate(selectedImage.timestamp)}
                </Typography>
                {selectedImage.metadata?.transferType && (
                  <Typography variant="body2" color="text.secondary">
                    Typ transferu: {selectedImage.metadata.transferType}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseMaskDialog}>Zamknij</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Metadata Dialog */}
      <Dialog
        open={metadataDialogOpen}
        onClose={handleCloseMetadataDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedImage && (
          <>
            <DialogContent>
              <Typography variant="h6" gutterBottom>
                Metadane obrazu
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>URL:</strong> {selectedImage.url}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Data wygenerowania:</strong> {formatDate(selectedImage.timestamp)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>ID:</strong> {selectedImage.id}
                </Typography>
                
                {selectedImage.metadata && (
                  <>
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                      Parametry generowania
                    </Typography>
                    {selectedImage.metadata.quality && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Jakość:</strong> {selectedImage.metadata.quality === 'high' ? 'Wysoka' : 'Standardowa'}
                      </Typography>
                    )}
                    {selectedImage.metadata.modelUsed && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Model:</strong> {selectedImage.metadata.modelUsed}
                      </Typography>
                    )}
                    {selectedImage.metadata.transferType && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Typ transferu:</strong> {selectedImage.metadata.transferType}
                      </Typography>
                    )}
                    {selectedImage.metadata.elapsedTime && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Czas generowania:</strong> {selectedImage.metadata.elapsedTime}s
                      </Typography>
                    )}
                    {selectedImage.metadata.width && selectedImage.metadata.height && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Wymiary:</strong> {selectedImage.metadata.width} × {selectedImage.metadata.height} px
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseMetadataDialog}>Zamknij</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default GeneratedImageViewer;
